import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Typography, Grid, TextField, MenuItem, Box, Button, Paper, Snackbar, Alert, LinearProgress } from '@mui/material';
import { getData, postData } from '../../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../../services/endpoints';

const AssignPolicies = ({ initialData, navigationContext, userId: userIdProp, onSaveAndReturn, onUpdateEmployeeSuccess, onFormCompletionChange }) => {
    const [searchParams] = useSearchParams();
    const userId = userIdProp || searchParams.get("employeeId");
    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const orgId = userData?.organization_id;
    const [assignments, setAssignments] = useState({
        leavePolicy: '',
        shiftType: '',
        attendanceSource: '',
        holidayPolicy: '',
    });

    const [leavePolicyTypes, setLeavePloicyTypes] = useState([]);
    const [shiftTypes, setShiftTypes] = useState([]);
    const [attendanceSource, setAttendanceSource] = useState([]);
    const [holidayPolicyTypes, setHolidayPolicyTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [completion, setCompletion] = useState(0);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const fetchEmployeePolicyAssignment = useCallback(async () => {
        if (!userId) return;
        try {
            const response = await getData(`${API_ENDPOINTS.fetchUserDetails}?userId=${userId}`);
            if (response?.status === 'SUCCESS') {
                const assignment = response.message.user?.policy_assignment;
                if (assignment) {
                    setAssignments({
                        leavePolicy: assignment.leave_policy_id || '',
                        shiftType: assignment.shift_type_id || '',
                        attendanceSource: assignment.attendance_source_id || '',
                        holidayPolicy: assignment.holiday_policy_id || '',
                    });
                }
            }
        } catch (error) {
            // No existing assignment yet or fetch failed; dropdowns stay unset.
        }
    }, [userId]);

    useEffect(() => {
        if (leavePolicyTypes.length === 0 || shiftTypes.length === 0 || attendanceSource.length === 0 || holidayPolicyTypes.length === 0) {
            return;
        }
        if (initialData) {
            const data = initialData.user || initialData;
            setAssignments({
                leavePolicy: data.leave_policy_id || '',
                shiftType: data.shift_type_id || '',
                attendanceSource: data.attendance_source_id || '',
                holidayPolicy: data.holiday_policy_id || '',
            });
            return;
        }
        fetchEmployeePolicyAssignment();
    }, [initialData, leavePolicyTypes, shiftTypes, attendanceSource, holidayPolicyTypes, fetchEmployeePolicyAssignment]);

    const calculateProgress = React.useCallback(() => {
        const allFields = ['leavePolicy', 'shiftType', 'attendanceSource', 'holidayPolicy'];

        const filledCount = allFields.filter(field => {
            const val = assignments[field];
            return val !== '' && val !== null && val !== undefined;
        }).length;

        const percent = Math.round((filledCount / allFields.length) * 100);
        setCompletion(percent);

        if (typeof onFormCompletionChange === 'function') {
            onFormCompletionChange(percent);
        }
    }, [assignments, onFormCompletionChange]);

    // Recalculate progress when assignments change
    useEffect(() => {
        calculateProgress();
    }, [calculateProgress]);

    // Fetch all policy data from APIs
    useEffect(() => {
        if (!orgId) return;

        const fetchAllPolicyData = async () => {
            try {
                const leaveResponse = await getData(API_ENDPOINTS.getAllLeavePoliciesByOrganizationId(orgId))

                if (leaveResponse?.status === 'SUCCESS') {
                    setLeavePloicyTypes(Array.isArray(leaveResponse.result) ? leaveResponse.result : [])
                }

                const shiftResponse = await getData(API_ENDPOINTS.getAllShiftTypesByOrganizationId(orgId))

                if (shiftResponse?.status === 'SUCCESS') {
                    setShiftTypes(Array.isArray(shiftResponse.result) ? shiftResponse.result : [])
                }

                const attendanceResponse = await getData(API_ENDPOINTS.getAllAttendanceSourcesByOrganizationId(orgId))

                if (attendanceResponse?.status === 'SUCCESS') {
                    setAttendanceSource(Array.isArray(attendanceResponse.result) ? attendanceResponse.result : [])
                }

                const holidayResponse = await getData(API_ENDPOINTS.getAllHolidayPoliciesByOrganizationId(orgId))

                if (holidayResponse?.status === 'SUCCESS') {
                    setHolidayPolicyTypes(Array.isArray(holidayResponse.result) ? holidayResponse.result : [])
                }

            } catch (error) {
                console.error('Failed to fetch policy dropdown data:', error);
                setSnackbar({
                    open: true,
                    message: 'Failed to load policy options. Please refresh and try again.',
                    severity: 'error'
                });
            }
        };

        fetchAllPolicyData();
    }, [orgId]);

    const handleSnackbarClose = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Handler for all policy dropdown changes
    const handleChange = (event) => {
        const { name, value } = event.target;
        setAssignments(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSaveChanges = async () => {
        if (!assignments.leavePolicy || !assignments.shiftType || !assignments.attendanceSource || !assignments.holidayPolicy) {
            setSnackbar({
                open: true,
                message: 'Required Fields are missing',
                severity: 'error'
            })
            return;
        }

        setLoading(true);
        try {
            // Prepare API payload
            const apiPayload = {
                user_id: userId,
                leave_policy_id: assignments.leavePolicy,
                shift_type_id: assignments.shiftType,
                attendance_source_id: assignments.attendanceSource,
                holiday_policy_id: assignments.holidayPolicy,
            };

            try {
                const response = await postData(API_ENDPOINTS.updateEmployeePolicyAssignment, apiPayload)

                if (response.status === 'SUCCESS') {
                    setSnackbar({
                        open: true,
                        message: response.message || 'Policy assignments saved successfully!',
                        severity: 'success'
                    });
                    await fetchEmployeePolicyAssignment();
                    onUpdateEmployeeSuccess?.();
                    if (navigationContext?.returnToRehire && onSaveAndReturn) {
                        onSaveAndReturn();
                    }
                    return true;
                } else {
                    setSnackbar({
                        open: true,
                        message: response.message || 'Failed to update policy assignments',
                        severity: 'error'
                    });
                    return false;
                }
            } catch (error) {
                setSnackbar({
                    open: true,
                    message: error?.data?.message || 'Failed to update policy assignments',
                    severity: 'error'
                });
                return false;
            }


        } catch (error) {
            setSnackbar({
                open: true,
                message: 'Failed to save policy assignments',
                severity: 'error'
            });
            return false;
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 2 }}>
                <Typography variant="h6" component="h2" fontWeight={600} sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
                    Policy Assignment
                </Typography>
                <Box sx={{ width: { xs: 110, sm: 220 }, textAlign: 'right' }}>
                    <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' } }}>
                        {`Form Completion: ${completion}%`}
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={completion}
                        sx={{
                            height: 8,
                            borderRadius: 5,
                            mt: 0.5,
                            backgroundColor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: completion === 100 ? '#2ecc71' : '#1976d2'
                            }
                        }}
                    />
                </Box>
            </Box>

            <Grid container spacing={3} sx={{ mt: 2 }}>
                {/* 1. Leave Policy Dropdown */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                        select
                        fullWidth
                        required
                        size="small"
                        label="Leave Policy"
                        name="leavePolicy"
                        value={assignments.leavePolicy}
                        onChange={handleChange}
                        variant="outlined"
                        disabled={loading}
                    >
                        {leavePolicyTypes.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                                {option.leave_policy_name}
                            </MenuItem>
                        ))
                        }
                    </TextField>
                </Grid>

                {/* 2. Shift Type Dropdown */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                        select
                        fullWidth
                        required
                        size="small"
                        label="Shift Type"
                        name="shiftType"
                        value={assignments.shiftType}
                        onChange={handleChange}
                        variant="outlined"
                        disabled={loading}
                    >
                        {shiftTypes.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                                {option.shift_name}
                            </MenuItem>
                        ))
                        }
                    </TextField>
                </Grid>

                {/* 3. Attendance Source Dropdown */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                        select
                        fullWidth
                        required
                        size="small"
                        label="Attendance Source"
                        name="attendanceSource"
                        value={assignments.attendanceSource}
                        onChange={handleChange}
                        variant="outlined"
                        disabled={loading}
                    >
                        {attendanceSource.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                                {option.source_name}
                            </MenuItem>
                        ))
                        }
                    </TextField>
                </Grid>

                {/* 4. Holiday Policy Dropdown */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                        select
                        fullWidth
                        required
                        size="small"
                        label="Holiday Policy"
                        name="holidayPolicy"
                        value={assignments.holidayPolicy}
                        onChange={handleChange}
                        variant="outlined"
                        disabled={loading}
                    >
                        {holidayPolicyTypes.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                                {option.holiday_policy_name}
                            </MenuItem>
                        ))
                        }
                    </TextField>
                </Grid>
            </Grid>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                {navigationContext?.returnToRehire ? (
                    <Paper
                        elevation={3}
                        sx={{
                            width: '100%',
                            p: 2,
                            bgcolor: 'info.light',
                            borderLeft: '4px solid',
                            borderColor: 'info.main'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    Rehire Verification in Progress
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Completing verification for: <strong>{navigationContext.stepLabel || 'Policy Assignment'}</strong>
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: { xs: '0.7rem', md: '0.875rem' } }}>
                                    Form Completion: <strong>{completion}%</strong>
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                color="info"
                                onClick={async () => {
                                    const success = await handleSaveChanges();
                                    if (success && onSaveAndReturn) {
                                        onSaveAndReturn();
                                    }
                                }}
                                disabled={loading}
                            >
                                {loading ? 'Saving...' : 'Verify & Return to Rehire'}
                            </Button>
                        </Box>
                    </Paper>
                ) : (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveChanges}
                        sx={{ py: 1.5, px: 4 }}
                        disabled={loading}
                    >
                        {loading ? 'Saving...' : 'Save Details'}
                    </Button>
                )}
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AssignPolicies;